/* Searchable combobox with optional inline create. */
import { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronsUpDown, Loader2, Plus, Search } from "lucide-react";

export function Combobox({ value, onChange, options, placeholder = "Select…", searchPlaceholder = "Search…", testid, onCreate, loading = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef();

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(ql) || (o.sub || "").toLowerCase().includes(ql));
  }, [q, options]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => !loading && setOpen((v) => !v)}
        disabled={loading}
        className={`h-9 w-full rounded-md border border-slate-300 text-xs px-2 bg-white text-left flex items-center justify-between gap-2 ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
        data-testid={`${testid}-trigger`}
      >
        <span className={selected && !loading ? "" : "text-slate-400"}>
          {loading ? "Loading…" : selected ? selected.label : placeholder}
        </span>
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 animate-spin" />
        ) : (
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        )}
      </button>

      {open && !loading && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
          <div className="p-1.5 border-b border-slate-100 relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full h-7 pl-7 pr-2 text-xs rounded border border-slate-200 outline-none focus:border-[var(--erp-primary)]"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-56 overflow-auto">
            {filtered.length === 0 && (
              <div className="p-3 text-xs text-slate-400 text-center">
                No match.
                {onCreate && q.trim() && (
                  <button
                    className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-dashed border-slate-300 hover:bg-slate-50 erp-text-primary text-xs font-semibold"
                    onClick={async () => { const created = await onCreate(q.trim()); if (created) { onChange(created); setOpen(false); setQ(""); } }}
                    data-testid={`${testid}-create`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add "{q}"
                  </button>
                )}
              </div>
            )}
            {filtered.map((o) => (
              <button
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between gap-2 ${o.value === value ? "bg-indigo-50" : ""}`}
                data-testid={`${testid}-opt-${o.value}`}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{o.label}</div>
                  {o.sub && <div className="text-[10px] text-slate-500 truncate">{o.sub}</div>}
                </div>
                {o.value === value && <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MultiCombobox({ value = [], onChange, options, placeholder = "Select…", testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef();
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(ql));
  }, [q, options]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const selectedLabels = options.filter((o) => value.includes(o.value)).map((o) => o.label);
  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="h-9 w-full rounded-md border border-slate-300 text-xs px-2 bg-white text-left flex items-center justify-between gap-2 overflow-hidden">
        <span className={value.length ? "truncate" : "text-slate-400"}>
          {value.length ? (value.length > 2 ? `${value.length} selected` : selectedLabels.join(", ")) : placeholder}
        </span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
          <div className="p-1.5 border-b border-slate-100 relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full h-7 pl-7 pr-2 text-xs rounded border border-slate-200 outline-none" />
          </div>
          <div className="max-h-56 overflow-auto">
            {filtered.map((o) => (
              <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} className="w-3.5 h-3.5" />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
